/**
 * AgentModeIndicator — Floating banner showing AI navigation state.
 *
 * Appears when the AI orchestrator is actively navigating the portal
 * (DEC-026 Tier 1). Shows current page, highlight count, and a kill switch.
 * Fixed position bottom-right, auto-hides when inactive.
 */

import React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Chip from '@mui/material/Chip'
import Fade from '@mui/material/Fade'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import StopCircleIcon from '@mui/icons-material/StopCircle'
import { useTheme } from '@mui/material/styles'

interface AgentModeIndicatorProps {
  isAgentActive: boolean
  currentAgentPage: string
  highlightCount: number
  stopAgent: () => void
}

const AgentModeIndicator: React.FC<AgentModeIndicatorProps> = ({
  isAgentActive,
  currentAgentPage,
  highlightCount,
  stopAgent,
}) => {
  const theme = useTheme()
  return (
    <Fade in={isAgentActive}>
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 9999,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.85)' : 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(8px)',
          borderRadius: 3,
          p: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          color: 'text.primary',
          border: '1px solid',
          borderColor: 'divider',
          maxWidth: 360,
        }}
      >
        <SmartToyIcon sx={{ color: 'primary.main', fontSize: 20 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}
          >
            AI is navigating
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {currentAgentPage || 'Home'}
          </Typography>
        </Box>
        {highlightCount > 0 && (
          <Chip
            label={highlightCount}
            size="small"
            sx={{
              bgcolor: theme.palette.warning.main,
              color: theme.palette.warning.contrastText,
              fontWeight: 600,
              height: 22,
            }}
          />
        )}
        <IconButton
          size="small"
          onClick={stopAgent}
          sx={{
            color: 'error.main',
            '&:hover': { bgcolor: 'error.main' + '1A' },
          }}
        >
          <StopCircleIcon fontSize="small" />
        </IconButton>
      </Box>
    </Fade>
  )
}

export default AgentModeIndicator
